/**
 * LoadingContext - Sistema centralizado de seguimiento de carga de componentes
 * 
 * Cada componente asíncrono (tarjetas, gráficos, reloj, etc.) debe registrarse
 * y reportar cuando está cargado. El progreso global solo llega a 100% cuando
 * TODOS los componentes registrados están listos.
 */
import { createContext, useContext, useState, useCallback, useMemo, useRef, useEffect } from 'react';

// Definición de componentes que deben cargarse
// Cada componente tiene un peso relativo para el cálculo del progreso
// NOTA: Solo componentes que se renderizan en el dashboard principal (no ventanas flotantes)
const COMPONENT_WEIGHTS = {
  // Componentes principales del dashboard
  'markets': 25,          // Lista de mercados/tarjetas (datos iniciales)
  'posiblesInversiones': 10, // Posibles Inversiones (se carga con la cartera)
  'sparklines': 15,       // Sparklines de los mercados (se cargan después)
  'candlestick': 10,      // Gráfico de velas principal (peso reducido: safety-timer lo desbloquea en 3s)
  'clock': 5,             // Reloj/contador de actualización
  'sectors': 15,          // Distribución de sectores (si visible)
  // Nota: portfolio, volume, stockInfo son ventanas flotantes - no bloquean carga inicial
};

const LoadingContext = createContext(null);

export const LoadingProvider = ({ children }) => {
  // Estado de cada componente: { componentId: { loaded: boolean, progress: number } }
  const [componentStates, setComponentStates] = useState({});
  
  // Componentes registrados actualmente
  const [registeredComponents, setRegisteredComponents] = useState(new Set());
  
  // Flag para saber si al menos un componente ha empezado a cargar
  const [hasStartedLoading, setHasStartedLoading] = useState(false);
  
  // Ref para evitar actualizaciones duplicadas
  const lastProgressRef = useRef(0);
  
  // Registrar un componente para tracking
  const registerComponent = useCallback((componentId) => {
    setRegisteredComponents(prev => {
      const newSet = new Set(prev);
      newSet.add(componentId);
      return newSet;
    });
    setComponentStates(prev => ({
      ...prev,
      [componentId]: { loaded: false, progress: 0 }
    }));
    setHasStartedLoading(true);
  }, []);
  
  // Desregistrar un componente (cuando se desmonta)
  const unregisterComponent = useCallback((componentId) => {
    setRegisteredComponents(prev => {
      const newSet = new Set(prev);
      newSet.delete(componentId);
      return newSet;
    });
    setComponentStates(prev => {
      const newState = { ...prev };
      delete newState[componentId];
      return newState;
    });
  }, []);
  
  // Actualizar progreso de un componente específico (0-100)
  const updateComponentProgress = useCallback((componentId, progress) => {
    setComponentStates(prev => ({
      ...prev,
      [componentId]: { 
        ...prev[componentId],
        progress: Math.min(100, Math.max(0, progress)),
        loaded: progress >= 100
      }
    }));
  }, []);
  
  // Marcar un componente como completamente cargado
  const markComponentLoaded = useCallback((componentId) => {
    setComponentStates(prev => ({
      ...prev,
      [componentId]: { loaded: true, progress: 100 }
    }));
  }, []);
  // Marcar un componente como cargado FORZADAMENTE por el safety-timeout, no porque
  // los datos realmente terminaron de llegar (ej. yfinance rate-limited). Cuenta como
  // "loaded" para no bloquear la barra de progreso, pero queda marcado como forced
  // para que la UI pueda avisar de que la carga fue parcial.
  const markComponentForced = useCallback((componentId) => {
    setComponentStates(prev => ({
      ...prev,
      [componentId]: { loaded: true, progress: 100, forced: true }
    }));
  }, []);
  
  // Marcar un componente como NO cargado (reset)
  const markComponentLoading = useCallback((componentId) => {
    setComponentStates(prev => ({
      ...prev,
      [componentId]: { loaded: false, progress: 0 }
    }));
  }, []);
  
  // Calcular progreso global basado en componentes registrados y sus pesos
  const globalProgress = useMemo(() => {
    if (registeredComponents.size === 0) return 0;
    
    let totalWeight = 0;
    let loadedWeight = 0;
    
    registeredComponents.forEach(componentId => {
      const weight = COMPONENT_WEIGHTS[componentId] || 10; // Default weight of 10
      totalWeight += weight;
      
      const state = componentStates[componentId];
      if (state) {
        // Usar el progreso parcial del componente
        loadedWeight += (weight * (state.progress / 100));
      }
    });
    
    if (totalWeight === 0) return 0;
    
    const progress = Math.round((loadedWeight / totalWeight) * 100);
    return Math.min(100, progress);
  }, [componentStates, registeredComponents]);
  
  // Determinar si todos los componentes están cargados
  const isFullyLoaded = useMemo(() => {
    if (registeredComponents.size === 0) return false;
    
    for (const componentId of registeredComponents) {
      const state = componentStates[componentId];
      if (!state || !state.loaded) {
        return false;
      }
    }
    return true;
  }, [componentStates, registeredComponents]);
  // Determinar si algun componente llego a "cargado" por el safety-timeout en vez de
  // datos reales completos (ej. yfinance rate-limited). Sirve para que la UI muestre
  // un aviso honesto en vez de un 100% / "listo" sin matices.
  const hasForcedComponents = useMemo(() => {
    for (const componentId of registeredComponents) {
      if (componentStates[componentId]?.forced) return true;
    }
    return false;
  }, [componentStates, registeredComponents]);

  // Determinar si TODO está cargado DE VERDAD: todos los componentes loaded y
  // NINGUNO forzado por el safety-timeout. El indicador solo dice "listo al 100%"
  // cuando esto es true (petición del usuario: no anunciar listo antes de tiempo).
  const reallyFullyLoaded = useMemo(() => {
    if (registeredComponents.size === 0) return false;
    for (const componentId of registeredComponents) {
      const s = componentStates[componentId];
      if (!s || !s.loaded || s.forced) return false;
    }
    return true;
  }, [componentStates, registeredComponents]);
  
  // Determinar si está cargando (al menos un componente no está listo)
  const isLoading = useMemo(() => {
    if (!hasStartedLoading) return false;
    return !isFullyLoaded;
  }, [hasStartedLoading, isFullyLoaded]);
  
  // Resetear todo (para nueva sesión o refresh)
  const resetAll = useCallback(() => {
    setComponentStates({});
    setRegisteredComponents(new Set());
    setHasStartedLoading(false);
    lastProgressRef.current = 0;
  }, []);

  // -------------------------------------------------------------------------
  // Safety-timeout GLOBAL (feb 2026): en móvil algunos componentes pueden
  // no reportar `loaded=true` nunca (yfinance rate-limited, service worker
  // desactualizado, red flaky). El resultado era un overlay bloqueado al
  // 100 % con `isFullyLoaded=false`. Este efecto marca los componentes
  // restantes como `forced` transcurridos 55 s desde que empezó la carga
  // — la app se muestra "lista con avisos" (ámbar), y los datos siguen llegando en
  // segundo plano sin bloquear la UI.
  // -------------------------------------------------------------------------
  useEffect(() => {
    if (!hasStartedLoading) return;
    const t = setTimeout(() => {
      // Snapshot dentro del timer para que el force capture el estado ACTUAL
      // de componentes al vencer el timeout (no el estado al montar el efecto).
      setComponentStates(prev => {
        const next = { ...prev };
        let changed = false;
        registeredComponents.forEach(id => {
          const s = next[id];
          if (!s || !s.loaded) {
            next[id] = { loaded: true, progress: 100, forced: true };
            changed = true;
          }
        });
        return changed ? next : prev;
      });
    }, 55000);
    return () => clearTimeout(t);
  }, [hasStartedLoading, registeredComponents]);
  
  // Debug: log de estado de componentes
  useEffect(() => {
    if (globalProgress !== lastProgressRef.current) {
      lastProgressRef.current = globalProgress;
      console.log(`📊 Progreso Global: ${globalProgress}%`, {
        componentes: Object.fromEntries(
          Array.from(registeredComponents).map(id => [
            id, 
            componentStates[id]?.progress || 0
          ])
        )
      });
    }
  }, [globalProgress, componentStates, registeredComponents]);
  
  const value = {
    // Estados
    componentStates,
    registeredComponents: Array.from(registeredComponents),
    globalProgress,
    isLoading,
    isFullyLoaded,
    reallyFullyLoaded,
    hasStartedLoading,
    hasForcedComponents,
    
    // Acciones
    registerComponent,
    unregisterComponent,
    updateComponentProgress,
    markComponentLoaded,
    markComponentForced,
    markComponentLoading,
    resetAll,
  };
  
  return (
    <LoadingContext.Provider value={value}>
      {children}
    </LoadingContext.Provider>
  );
};

// Hook para usar el contexto de carga
export const useLoading = () => {
  const context = useContext(LoadingContext);
  if (!context) {
    throw new Error('useLoading debe usarse dentro de LoadingProvider');
  }
  return context;
};

// Hook helper para componentes individuales
export const useComponentLoading = (componentId) => {
  const { 
    registerComponent, 
    unregisterComponent, 
    updateComponentProgress, 
    markComponentLoaded,
    markComponentForced,
    markComponentLoading,
    componentStates 
  } = useLoading();
  
  // Registrar al montar, desregistrar al desmontar
  useEffect(() => {
    registerComponent(componentId);
    return () => unregisterComponent(componentId);
  }, [componentId, registerComponent, unregisterComponent]);
  
  const state = componentStates[componentId] || { loaded: false, progress: 0 };
  
  return {
    isLoaded: state.loaded,
    progress: state.progress,
    setProgress: (progress) => updateComponentProgress(componentId, progress),
    setLoaded: () => markComponentLoaded(componentId),
    setLoading: () => markComponentLoading(componentId),
  };
};

export default LoadingContext;
